import { useGetAssetStateCounts } from '@/hooks/api/assets/useGetAssetStateCounts';
import { LIST_ASSETS_BY_STATE_QUERY_KEY, useFetchAssetsByState } from '@/hooks/api/assets/useFetchAssetsByState';
import { AssetStateChart } from '@/components/AssetStateChart/AssetStateChart';
import { SearchTable } from '@/components/Search/SearchTable';
import { usePreserveDataDuringLoading } from '@/components/FilterData/hooks/usePreserveDataDuringLoading';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Box, styled } from '@mui/material';
import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AssetState } from '@/api/generatedApi';
import { AssetStateChartSkeleton } from '@/components/AssetStateChart/AssetStateChartSkeleton';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { getApiError } from '@/types/apiError';
import { useQueryClient } from '@tanstack/react-query';
import { isValidAssetState } from '@/utils/assetStateDisplayText';

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'row',
});

const ChartContainer = styled(Box)({
  width: '275px',
  flexShrink: 0,
  marginBottom: '16px',
});

const ASSET_STATE_PARAM = 'state';
const ASSETS_BY_STATE_MAX_PAGE_SIZE = 1000;

const TableBox = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const StatusPage = () => {
  usePageTitle('Status');
  const {
    data: assetStateCounts,
    isLoading,
    isRefetching,
    error,
  } = useGetAssetStateCounts({ pollWhileIndexing: true });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const assetStateParameter = searchParameters.get(ASSET_STATE_PARAM) ?? '';
  const selectedAssetState = isValidAssetState(assetStateParameter) ? assetStateParameter : AssetState.Finished;

  const selectAssetState = useCallback(
    (assetState: AssetState) => {
      const updatedParameters = new URLSearchParams(searchParameters);
      updatedParameters.set(ASSET_STATE_PARAM, assetState);
      setSearchParameters(updatedParameters);
    },
    [searchParameters, setSearchParameters]
  );

  const queryClient = useQueryClient();
  const preservedAssetStateCounts = usePreserveDataDuringLoading(assetStateCounts, isLoading || isRefetching);

  const assetsTotal = Object.entries(preservedAssetStateCounts || {})
    .filter(([key]) => isValidAssetState(key))
    .reduce((sum, [, count]) => sum + count, 0);

  const effectiveAssetState = useMemo<AssetState>(() => {
    const counts = preservedAssetStateCounts;
    if (!counts) return selectedAssetState;
    if ((counts[selectedAssetState as keyof typeof counts] ?? 0) > 0) return selectedAssetState;
    const firstWithCount = Object.entries(counts)
      .filter(([key]) => isValidAssetState(key))
      .find(([, count]) => count > 0);
    return (firstWithCount?.[0] as AssetState) ?? selectedAssetState;
  }, [preservedAssetStateCounts, selectedAssetState]);

  const { fetchData, error: assetsError } = useFetchAssetsByState({ assetState: effectiveAssetState });

  const tableSelectionCountReference = useRef(0);
  const previousCountsKeyReference = useRef<string | undefined>(undefined);

  const handleTableSelectionCountChange = useCallback(
    (count: number) => {
      const hadSelection = tableSelectionCountReference.current > 0;
      tableSelectionCountReference.current = count;
      if (hadSelection && count === 0) {
        queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
      }
    },
    [queryClient]
  );

  useEffect(() => {
    if (!assetStateCounts) return;
    const key = JSON.stringify(assetStateCounts);
    if (previousCountsKeyReference.current === key) return;
    previousCountsKeyReference.current = key;
    if (tableSelectionCountReference.current > 0) return;
    queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
  }, [assetStateCounts, queryClient]);

  if (error) {
    return <ApiErrorAlert error={getApiError(error)} />;
  }

  return (
    <ContentArea>
      <Box sx={{ width: '275px', flexShrink: 0 }}>
        {preservedAssetStateCounts ? (
          <ChartContainer>
            <AssetStateChart
              selectedAssetState={effectiveAssetState}
              data={preservedAssetStateCounts}
              assetsTotal={assetsTotal}
              onStateClick={selectAssetState}
            />
          </ChartContainer>
        ) : (
          <AssetStateChartSkeleton />
        )}
      </Box>
      <TableBox>
        <Box flex={1} minHeight={0} padding='6px 6px 6px 0'>
          <SearchTable
            fetchMatches={fetchData}
            isLoading={isLoading || isRefetching}
            error={assetsError}
            shouldShowSearchTabs={false}
            bulkSelectionKey={effectiveAssetState}
            selectAllPageSize={ASSETS_BY_STATE_MAX_PAGE_SIZE}
            onSelectionCountChange={handleTableSelectionCountChange}
            showFailureDiagnostics={effectiveAssetState === AssetState.Failed}
          />
        </Box>
      </TableBox>
    </ContentArea>
  );
};
